// src/hooks/usePropertyTypes.ts
import {useState, useCallback, useMemo} from "react";
import {
  useGetPropertyTypesQuery,
  useGetPropertyTypeByIdQuery,
  useCreatePropertyTypeMutation,
  useUpdatePropertyTypeMutation,
  useDeletePropertyTypeMutation,
  useGetPropertyTypeStatsQuery,
} from "../store/api/propertyTypesApi";
import {useToast} from "./common/useToast";
import {useConfirm} from "./common/useConfirm";
import type {
  PropertyTypeFilterDto,
  CreatePropertyTypeDto,
  UpdatePropertyTypeDto,
} from "../types/property-type";

export const usePropertyTypes = (filters: PropertyTypeFilterDto = {}) => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Query hooks
  const {
    data: propertyTypesResponse,
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useGetPropertyTypesQuery(filters);

  const {data: statsData, isLoading: statsLoading} =
    useGetPropertyTypeStatsQuery();

  // Mutation hooks
  const [createPropertyType, {isLoading: isCreating}] =
    useCreatePropertyTypeMutation();
  const [updatePropertyType, {isLoading: isUpdating}] =
    useUpdatePropertyTypeMutation();
  const [deletePropertyType, {isLoading: isDeleting}] =
    useDeletePropertyTypeMutation();

  // Memoized values
  const propertyTypes = useMemo(
    () => propertyTypesResponse?.data || [],
    [propertyTypesResponse]
  );
  const pagination = useMemo(
    () => propertyTypesResponse?.pagination,
    [propertyTypesResponse]
  );
  const stats = useMemo(
    () => propertyTypesResponse?.stats || statsData,
    [propertyTypesResponse, statsData]
  );

  // Handlers
  const handleCreate = useCallback(
    async (data: CreatePropertyTypeDto) => {
      try {
        const result = await createPropertyType(data).unwrap();
        success("Success", "Property type created successfully");
        return result;
      } catch (err) {
        error("Error", "Failed to create property type");
        throw err;
      }
    },
    [createPropertyType, success, error]
  );

  const handleUpdate = useCallback(
    async (id: number, data: UpdatePropertyTypeDto) => {
      try {
        await updatePropertyType({id, data}).unwrap();
        success("Success", "Property type updated successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to update property type");
        return false;
      }
    },
    [updatePropertyType, success, error]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await confirm({
        title: "Delete Property Type",
        message:
          "Are you sure you want to delete this property type? All associated properties will be affected.",
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return false;

      try {
        await deletePropertyType(id).unwrap();
        success("Success", "Property type deleted successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to delete property type");
        return false;
      }
    },
    [deletePropertyType, confirm, success, error]
  );

  return {
    // Data
    propertyTypes,
    pagination,
    stats,

    // Loading states
    isLoading: isLoading || isFetching,
    statsLoading,
    isCreating,
    isUpdating,
    isDeleting,

    // Error
    error: fetchError,

    // Actions
    refetch,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};

// Hook for single property type
export const usePropertyType = (id: number | undefined) => {
  const {
    data: propertyType,
    isLoading,
    error,
    refetch,
  } = useGetPropertyTypeByIdQuery(id!, {
    skip: !id,
  });

  return {
    propertyType,
    isLoading,
    error,
    refetch,
  };
};
